/**
 * MessageActions Component
 *
 * Reusable action button groups for chat messages matching the Figma design.
 * Features primary actions, secondary actions with visual spacer, and tooltips.
 *
 * Features:
 * - Primary actions group (copy, bookmark, edit, delete, etc.)
 * - Secondary actions group (like, dislike)
 * - Visual spacer between groups
 * - Consistent tooltip positioning
 * - Accessibility compliance
 * - Different layouts for user/assistant messages
 */

import React, { useState } from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Tooltip,
  Popover,
  Modal,
  Text,
} from '@mantine/core';
import {
  IconCopy,
  IconBookmark,
  IconBookmarkFilled,
  IconDeviceFloppy,
  IconEdit,
  IconTrash,
  IconVolume,
  IconRefresh,
  IconThumbUp,
  IconThumbDown,
  IconDots,
} from '@tabler/icons-react';
import classes from './MessageActions.module.css';
import { UserFeedbackModal, type FeedbackFormData, type FeedbackType } from '../UserFeedbackModal';
import { useDetailedFeedback } from '../../../../hooks/useFeedbackMutation';
import { transformFeedbackData, validateFeedbackData, type MessageForFeedback } from '../../../../utils/feedbackTransform';


// =============================================================================
// TYPES
// =============================================================================

export interface MessageAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

export interface MessageActionsProps {
  primaryActions?: MessageAction[];
  secondaryActions?: MessageAction[];
  menuActions?: MessageAction[];
  layout?: 'horizontal' | 'vertical';
  showSpacer?: boolean;
  className?: string;
  style?: React.CSSProperties;
  // Required for feedback functionality
  message?: MessageForFeedback;
  conversationId?: string;
  userName?: string;
}

// =============================================================================
// PREDEFINED ACTION CREATORS
// =============================================================================

export const createCopyAction = (
  onClick: () => void,
  isActive = false
): MessageAction => ({
  id: 'copy',
  icon: <IconCopy size={24} />,
  label: isActive ? 'Copied!' : 'Copy',
  onClick,
  isActive,
});

export const createSaveAction = (
  onClick: () => void,
  isBookmarked = false
): MessageAction => ({
  id: 'bookmark',
  icon: <IconDeviceFloppy size={24} />,
  label: isBookmarked ? 'Saved' : 'Save',
  onClick,
  isActive: isBookmarked,
});

export const createEditAction = (onClick: () => void, isDisabled: boolean): MessageAction => ({
  id: 'edit',
  icon: <IconEdit size={24} />,
  label: 'Edit',
  onClick,
  disabled: isDisabled
});

export const createDeleteAction = (onClick: () => void): MessageAction => ({
  id: 'delete',
  icon: <IconTrash size={24} />,
  label: 'Delete',
  onClick,
  variant: 'danger',
});

export const createPlayAudioAction = (
  onClick: () => void,
  isPlaying = false
): MessageAction => ({
  id: 'play_audio',
  icon: <IconVolume size={24} />,
  label: isPlaying ? 'Stop audio' : 'Play audio',
  onClick,
  isActive: isPlaying,
});

export const createRegenerateAction = (onClick: () => void): MessageAction => ({
  id: 'regenerate',
  icon: <IconRefresh size={24} />,
  label: 'Regenerate response',
  onClick,
});

export const createLikeAction = (
  onClick: () => void,
  isLiked = false
): MessageAction => ({
  id: 'like',
  icon: <IconThumbUp size={24} />,
  label: 'Good response',
  onClick,
  isActive: isLiked,
  variant: 'secondary',
});

export const createDislikeAction = (
  onClick: () => void,
  isDisliked = false
): MessageAction => ({
  id: 'dislike',
  icon: <IconThumbDown size={24} />,
  label: 'Poor response',
  onClick,
  isActive: isDisliked,
  variant: 'secondary',
});

export const createShowThoughtProcessAction = (onClick: () => void): MessageAction => ({
  id: 'show_thought_process',
  icon: null, // No icon as per Figma design
  label: 'Show Thinking',
  onClick,
});

export const createBranchInNewChatAction = (
  onClick: () => void,
  disabled = false
): MessageAction => ({
  id: 'branch_in_new_chat',
  icon: null,
  label: 'Branch in new chat',
  onClick,
  disabled,
});

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MessageActions: React.FC<MessageActionsProps> = ({
  primaryActions = [],
  secondaryActions = [],
  menuActions = [],
  layout = 'horizontal',
  showSpacer = true,
  className,
  style,
  message,
  conversationId,
  userName,
}) => {
  const [copiedRecently, setCopiedRecently] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [feedbackModalState, setFeedbackModalState] = useState<{
    isOpen: boolean;
    type: FeedbackType | null;
  }>({ isOpen: false, type: null });

  // Initialize feedback mutation hook
  const feedbackMutation = useDetailedFeedback(
    message?.id || '',
    conversationId || '',
  );

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleActionClick = (action: MessageAction) => {
    // Handle special actions that need modal/popover behavior
    if (action.id === 'like') {
      setFeedbackModalState({ isOpen: true, type: 'positive' });
      return;
    }

    if (action.id === 'dislike') {
      setFeedbackModalState({ isOpen: true, type: 'negative' });
      return;
    }

    action.onClick();

    // Handle copy feedback
    if (action.id === 'copy') {
      setCopiedRecently(true);
      setTimeout(() => setCopiedRecently(false), 2000);
    }
  };

  const handleFeedbackSubmit = async (data: FeedbackFormData) => {
    const currentFeedbackType = feedbackModalState.type;
    if (!currentFeedbackType) return;

    // Validate required context
    if (!message || !conversationId) {
      console.error('Missing required context for feedback submission:', { message, conversationId });
      setFeedbackModalState({ isOpen: false, type: null });
      return;
    }

    // Validate feedback data
    const validation = validateFeedbackData(data, currentFeedbackType);
    if (!validation.isValid) {
      console.error('Validation errors:', validation.errors);
      // TODO: Show validation errors to user
      return;
    }

    // Close modal immediately for better UX
    setFeedbackModalState({ isOpen: false, type: null });

    try {
      // extract geeie metadata from message if available
      const genieMetadata = message.genie_space_names || message.genie_conversation_id || message.genie_message_id ?
        {
          genie_space_names: message.genie_space_names,
          genie_conversation_id: message.genie_conversation_id,
          genie_message_id: message.genie_message_id,
        } : undefined;

      // Submit feedback via API
      const result = await feedbackMutation.submitDetailedFeedback(
        currentFeedbackType,
        {
          issue: data.issue,
          details: data.details,
          shareData: data.shareData,
        },
        message.content,
        // Extract user query from parent message
        message.parentMessage?.role === 'user' ? message.parentMessage.content : undefined,
        // include genie metadata for responses
        genieMetadata
      );

      if (result.success) {

        // Trigger the original like/dislike action for visual feedback
        const targetActionId = currentFeedbackType === 'positive' ? 'like' : 'dislike';
        const targetAction = secondaryActions.find(action => action.id === targetActionId);
        if (targetAction) {
          targetAction.onClick();
        }
      } else {
        console.error('Failed to submit feedback:', result.error);
        // TODO: Show error message to user
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      // TODO: Show error message to user
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackModalState({ isOpen: false, type: null });
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderAction = (action: MessageAction) => {
    const isActive = action.isActive || (action.id === 'copy' && copiedRecently);
    const buttonClassName = `${classes.actionButton} ${isActive ? classes[`${action.variant || 'primary'}Active`] : ''
      }`;

    return (
      <Tooltip
        key={action.id}
        label={action.id === 'copy' && copiedRecently ? 'Copied!' : action.label}
        position="top"
      >
        <ActionIcon
          variant="transparent"
          className={buttonClassName}
          onClick={() => handleActionClick(action)}
          disabled={action.disabled}
          aria-label={action.label}
          styles={{
            root: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            }
          }}
        >
          {action.icon}
        </ActionIcon>
      </Tooltip>
    );
  };

  const renderMenu = () => {
    if (menuActions.length === 0) return null;

    return (
      <Popover
        width={240}
        position="top-start"
        offset={12}
        shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
        opened={isPopoverOpen}
        onChange={setIsPopoverOpen}
        styles={{
          dropdown: {
            border: '1px solid var(--fonterra-border-accent)',
            borderRadius: '6px',
            backgroundColor: 'var(--fonterra-surface-popover)',
            padding: '9px 0px',
            boxShadow: 'var(--fonterra-shadow-popover)',
          }
        }}
      >
        <Popover.Target>
          <Tooltip label="More options" position="top">
            <ActionIcon
              variant="transparent"
              className={classes.actionButton}
              aria-label="More options"
              onClick={() => setIsPopoverOpen(true)}
              styles={{
                root: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                }
              }}
            >
              <IconDots size={24} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>

        <Popover.Dropdown>
          {menuActions.map((action) => (
            <Box
              key={action.id}
              className={classes.popoverItem}
              onClick={() => {
                handleActionClick(action);
                setIsPopoverOpen(false);
              }}
            >
              <Text
                className={classes.popoverItemText}
                style={{
                  color: action.variant === 'danger' ? 'var(--mantine-color-red-6)' : undefined,
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  opacity: action.disabled ? 0.5 : 1
                }}
              >
                {action.label}
              </Text>
            </Box>
          ))}
        </Popover.Dropdown>
      </Popover>
    );
  };

  const renderSpacer = () => {
    if (!showSpacer || secondaryActions.length === 0) return null;
    return <Box className={classes.spacer} />;
  };

  const renderFeedbackModal = () => {
    if (!feedbackModalState.type) return null;

    return (
      <Modal
        opened={feedbackModalState.isOpen}
        onClose={handleFeedbackCancel}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <UserFeedbackModal
          type={feedbackModalState.type}
          onSubmit={handleFeedbackSubmit}
          onCancel={handleFeedbackCancel}
          isSubmitting={feedbackMutation.isLoading}
          error={feedbackMutation.error?.message || null}
        />
      </Modal>
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  if (primaryActions.length === 0 && secondaryActions.length === 0 && menuActions.length === 0) {
    return null;
  }

  const containerClassName = `${classes.container} ${classes[layout]} ${className || ''}`;

  return (
    <Box className={containerClassName} style={style}>
      {/* All actions in single horizontal row as per Figma design */}
      <Group gap={0} wrap="nowrap">
        {/* Primary Actions */}
        {primaryActions.map(renderAction)}
        {renderMenu()}

        {/* Invisible Spacer */}
        {renderSpacer()}

        {/* Secondary Actions */}
        {secondaryActions.map(renderAction)}
      </Group>

      {/* Feedback Modal */}
      {renderFeedbackModal()}
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default MessageActions;
